import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  ArrowRight,
  Clock,
  FileText,
  Users,
  Building,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface Step {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  completed?: boolean;
  current?: boolean;
}

interface ProcessVisualizationProps {
  steps?: Step[];
  currentStep?: number;
  onStepClick?: (stepId: number) => void;
  title?: string;
  subtitle?: string;
}

const ProcessVisualization = ({
  steps = [
    {
      id: 1,
      title: "Free Consultation",
      description:
        "Discuss your business needs and get expert advice on the best setup options.",
      icon: <Users className="h-6 w-6" />,
      completed: true,
      current: false,
    },
    {
      id: 2,
      title: "Documentation",
      description:
        "Prepare and submit all required documents for your business registration.",
      icon: <FileText className="h-6 w-6" />,
      completed: true,
      current: false,
    },
    {
      id: 3,
      title: "Application Processing",
      description:
        "Your application is reviewed and processed by relevant authorities.",
      icon: <Clock className="h-6 w-6" />,
      completed: false,
      current: true,
    },
    {
      id: 4,
      title: "Company Formation",
      description:
        "Receive your trade license and complete the company registration process.",
      icon: <Building className="h-6 w-6" />,
      completed: false,
      current: false,
    },
  ],
  currentStep = 3,
  onStepClick = (stepId: number) => console.log(`Step ${stepId} clicked`),
  title = "Our Simple Process",
  subtitle = "Setting up your business in the UAE is straightforward with our step-by-step approach",
}: ProcessVisualizationProps) => {
  const [activeStep, setActiveStep] = useState<number>(currentStep);
  const [progress, setProgress] = useState<number>(0);

  useEffect(() => {
    // Calculate progress percentage based on current step
    const progressPercentage = ((activeStep - 1) / (steps.length - 1)) * 100;
    setProgress(progressPercentage);
  }, [activeStep, steps.length]);

  const handleStepClick = (stepId: number) => {
    setActiveStep(stepId);
    onStepClick(stepId);
  };

  return (
    <section className="w-full py-16 px-4 md:px-8 bg-slate-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-slate-900">
            {title}
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">{subtitle}</p>
        </div>

        <div className="relative mb-16">
          <Progress value={progress} className="h-2 w-full max-w-4xl mx-auto" />

          <div className="flex justify-between max-w-4xl mx-auto mt-2">
            {steps.map((step) => (
              <div
                key={step.id}
                className="relative"
                style={{
                  left: `${step.id === 1 ? "0%" : step.id === steps.length ? "0%" : "0%"}`,
                }}
              >
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleStepClick(step.id)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${step.id <= activeStep ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"}`}
                >
                  {step.id < activeStep ? (
                    <CheckCircle className="h-6 w-6" />
                  ) : (
                    <span className="text-sm font-bold">{step.id}</span>
                  )}
                </motion.button>
                <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                  <span
                    className={`text-sm font-medium ${step.id === activeStep ? "text-blue-600" : "text-slate-500"}`}
                  >
                    {step.title}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeStep}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="max-w-4xl mx-auto"
          >
            {steps.map(
              (step) =>
                step.id === activeStep && (
                  <Card key={step.id} className="bg-white shadow-lg border-0">
                    <CardContent className="p-8">
                      <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                        <div className="p-4 rounded-full bg-blue-100 text-blue-600">
                          {step.icon}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold mb-2 text-slate-900">
                            {step.title}
                          </h3>
                          <p className="text-slate-600">{step.description}</p>
                        </div>
                        {step.id < steps.length && (
                          <Button
                            onClick={() => handleStepClick(step.id + 1)}
                            className="mt-4 md:mt-0"
                          >
                            Next Step <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        )}
                        {step.id === steps.length && (
                          <Button
                            onClick={() => console.log("Start process clicked")}
                            className="mt-4 md:mt-0"
                          >
                            Start Process
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ),
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default ProcessVisualization;
